import { useState } from 'react';
import styled from 'styled-components';

import Spinner from 'src/components/spinner/Spinner';
import Div from 'src/components/div/Div';

import { fireActionsDueDateReminder, fireUnchangedActionStatusReminder } from 'src/utils/api';
import { BsSendCheck, BsSendX } from 'react-icons/bs';

const Reminders = () => {
  const initialLoadingStates = {};
  REMINDERS.forEach(element => {
    initialLoadingStates[element.id] = { isLoading: false, success: false, error: false };
  });

  const [loadingStates, setLoadingStates] = useState<{ [key: string]: { isLoading: boolean; success: boolean; error: boolean } }>(
    initialLoadingStates
  );

  const updateLoadingState = (id: string, isLoading: boolean, success: boolean, error: boolean) => {
    setLoadingStates(prevLoadingStates => ({
      ...prevLoadingStates,
      [id]: { isLoading, success, error },
    }));
  };

  const handleLunchReminders = async (element: IReminderElement) => {
    const { id, action } = element;
    updateLoadingState(id, true, false, false);
    action()
      .then(result => {
        updateLoadingState(id, false, true, false);
      })
      .catch(error => {
        updateLoadingState(id, false, false, true);
      })
      .finally(() => {
        setTimeout(() => {
          updateLoadingState(id, false, false, false);
        }, 2000);
      });
  };

  return (
    <Container>
      {REMINDERS.map((element, index) => {
        const { isLoading, success, error } = loadingStates[element.id];
        return (
          <Div key={index} flex gap='15px'>
            <ReminderContainer>
              <ReminderName>{element.label}</ReminderName>
              <ReminderActionsContainer>
                {isLoading ? (
                  <SpinnerContainer color='#326c82' />
                ) : (
                  <ReminderActions onClick={() => handleLunchReminders(element)}>Lancer</ReminderActions>
                )}
              </ReminderActionsContainer>
            </ReminderContainer>
            {(error || success) && (
              <ReminderResultContainer status={error ? 'error' : success ? 'success' : null}>
                {success && (
                  <Div flex alignItems='center' gap='15px'>
                    <BsSendCheck size={24} />
                    <p>Reminder launched successfully</p>
                  </Div>
                )}
                {error && (
                  <Div flex alignItems='center' gap='15px'>
                    <BsSendX size={24} />
                    <p>An error occurred</p>
                  </Div>
                )}
              </ReminderResultContainer>
            )}
          </Div>
        );
      })}
    </Container>
  );
};

export default Reminders;

// types
interface IReminderElement {
  id: string;
  label: string;
  action: () => Promise<any>;
}

// Constants
export const REMINDERS: IReminderElement[] = [
  {
    id: 'UNCHANGED_ACTION_STATUS',
    label: 'Relance des actions avec un status inchangées pour plus de 3 semaines.',
    action: fireUnchangedActionStatusReminder,
  },
  {
    id: 'PASSED_ACTION_DUE_DATE',
    label: "Relance des actions avec une date d'échéance dépassé.",
    action: fireActionsDueDateReminder,
  },
];

// StyledComponents

const Container = styled.div`
  & > *:not(:last-child) {
    margin-bottom: 15px;
  }
`;

const ReminderContainer = styled.div`
  height: 50px;
  width: 50%;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px 0 20px;
  border: 1px solid #e9edf7;
  column-gap: 20px;
`;

const ReminderName = styled.span`
  white-space: nowrap;
  font-size: 1.125em;
`;

const ReminderActionsContainer = styled.div`
  display: flex;
  column-gap: 20px;
`;

const ReminderActions = styled.button`
  color: white;
  font-weight: bold;
  cursor: pointer;
  padding: 10px 20px;
  border: 1px solid #00b894;
  border-radius: 12px;
  background-color: #00b894;
  opacity: 0.7;
  transition: opacity 0.2s ease-in-out;

  &:hover {
    opacity: 1;
  }
`;

const SpinnerContainer = styled(Spinner)`
  margin-right: 35px;
`;

const ReminderResultContainer = styled.div<{ status: 'success' | 'error' }>`
  height: 50px;
  width: 25%;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  column-gap: 20px;
  border: 1px solid ${props => getColor(props.status)};
  color: ${props => getColor(props.status)};
`;

// Utils

const getColor = (status: 'success' | 'error') => {
  switch (status) {
    case 'success':
      return '#87d37c';
    case 'error':
      return '#ef3e36';
    default:
      return '#e9edf7';
  }
};
